import { LitElement, html, nothing, type PropertyValues } from 'lit';
import styles from './styles.js';
import { property, query } from 'lit/decorators.js';
import type { Placement, TooltipTrigger } from '../types.js';
import { PlacementController } from '../utils/placement.controller.js';

const ARROW_HALF = 6;  // px — half the rotated arrow square's side length

/**
 * uwc-tooltip
 *
 * Hover / focus triggered tooltip with arrow indicator.
 * Supports plain text via `content` attribute and rich HTML via slot="content".
 * Show/hide delays prevent flicker on fast cursor sweeps.
 * Panel hover cancels the hide timer — allowing users to interact with
 * rich tooltip content such as links or code snippets.
 *
 * @slot trigger          - The element that triggers the tooltip.
 * @slot content          - Rich HTML tooltip content (overrides `content` attr).
 *
 * @attr content          - Plain text tooltip string.
 * @attr placement        - Default: top.
 * @attr offset           - Gap px. Default: 10 (slightly larger to clear arrow).
 * @attr show-delay       - ms before showing. Default: 400.
 * @attr hide-delay       - ms before hiding. Default: 100.
 * @attr disabled         - Prevent the tooltip from showing.
 * @attr arrow            - Show the placement arrow. Default: true.
 * @attr trigger          - Trigger mode: hover focus | hover | focus | click | manual.
 *                          Default: "hover focus".
 * @attr trigger-id       - External trigger element id.
 * @attr max-width        - Max panel width. Default: 260px.
 *
 * @csspart panel
 * @csspart arrow
 * @csspart text
 *
 * @cssprop --uwc-tooltip-bg            Default: #1c1c22
 * @cssprop --uwc-tooltip-color         Default: rgba(255,255,255,0.88)
 * @cssprop --uwc-tooltip-radius        Default: 7px
 * @cssprop --uwc-tooltip-shadow
 * @cssprop --uwc-tooltip-font-size     Default: 12px
 * @cssprop --uwc-tooltip-max-width     Default: 260px
 * @cssprop --uwc-tooltip-duration      Default: 120ms
 * @cssprop --uwc-tooltip-z             Default: 9999
 */
export class UwcTooltip extends LitElement {

  @property({ type: String                                   }) triggerId?: string;
  @property({ type: String                                   }) content?: string;
  @property({ type: String                                   }) placement: Placement = 'top';
  @property({ type: Number                                   }) offset = 10;
  @property({ type: Number,  attribute: 'show-delay'         }) showDelay = 400;
  @property({ type: Number,  attribute: 'hide-delay'         }) hideDelay = 100;
  @property({ type: Boolean, reflect: true                   }) disabled = false;
  @property({ type: Boolean                                  }) arrow = true;
  @property({ type: String,  attribute: 'trigger-mode'       }) triggerMode: TooltipTrigger = 'hover focus';
  @property({ type: String,  attribute: 'max-width'          }) maxWidth = '260px';

  @query('#panel') private _panel!: HTMLElement;
  @query('#arrow') private _arrow?: HTMLElement;

  private _showTimer: ReturnType<typeof setTimeout> | null = null;
  private _hideTimer: ReturnType<typeof setTimeout> | null = null;

  private _pc = new PlacementController(this, {
    getPanelEl: () => this._panel,

    onTriggerFound: (el) => {
      const cleanups: Array<() => void> = [];

      if (this.triggerMode.includes('hover')) {
        const onEnter = (): void => this._scheduleShow();
        const onLeave = (): void => this._scheduleHide();
        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('mouseleave', onLeave);
        cleanups.push(
          () => el.removeEventListener('mouseenter', onEnter),
          () => el.removeEventListener('mouseleave', onLeave),
        );
      }

      if (this.triggerMode.includes('focus')) {
        // Focus shows immediately — keyboard users should not wait for show-delay
        const onFocus = (): void => { this._cancelTimers(); this._pc.show(); };
        const onBlur  = (): void => { this._cancelTimers(); this._pc.hide(); };
        el.addEventListener('focus', onFocus);
        el.addEventListener('blur',  onBlur);
        cleanups.push(
          () => el.removeEventListener('focus', onFocus),
          () => el.removeEventListener('blur',  onBlur),
        );
      }

      if (this.triggerMode === 'click') {
        const onClick = (e: Event): void => { e.stopPropagation(); this._pc.toggle(); };
        el.addEventListener('click', onClick);
        cleanups.push(() => el.removeEventListener('click', onClick));
      }

      // Set ARIA relationship
      const panelId = 'panel';
      el.setAttribute('aria-describedby', panelId);

      return () => {
        cleanups.forEach(fn => fn());
        el.removeAttribute('aria-describedby');
      };
    },

    dismissOnEscape: true,

    afterPosition: ({ triggerRect, panelCoords, panelEl }) => {
      if (this.arrow) this._positionArrow(triggerRect, panelCoords, panelEl);
    },
  });

  // ── Lifecycle ────────────────────────────────────────────────────

  override firstUpdated(): void {
    this._pc.firstUpdated(
      this.triggerId ?? null,
      this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="trigger"]'),
    );

    // Panel hover: cursor into the tooltip cancels hide timer.
    // Wired once in firstUpdated because the panel is always in shadow DOM.
    const panel = this._panel;
    panel?.addEventListener('mouseenter', () => this._cancelTimers());
    panel?.addEventListener('mouseleave', () => this._scheduleHide());
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    this._pc.updated(
      changed as Map<PropertyKey, unknown>,
      this.triggerId ?? null,
      this.shadowRoot!.querySelector<HTMLSlotElement>('slot[name="trigger"]'),
    );
  }

  // ── Timer helpers ────────────────────────────────────────────────

  private _cancelTimers(): void {
    if (this._showTimer !== null) { clearTimeout(this._showTimer); this._showTimer = null; }
    if (this._hideTimer !== null) { clearTimeout(this._hideTimer); this._hideTimer = null; }
  }

  private _scheduleShow(): void {
    if (this.disabled) return;
    this._cancelTimers();
    this._showTimer = setTimeout(() => this._pc.show(), this.showDelay);
  }

  private _scheduleHide(): void {
    this._cancelTimers();
    this._hideTimer = setTimeout(() => this._pc.hide(), this.hideDelay);
  }

  // ── Arrow positioning ────────────────────────────────────────────

  /**
   * Position the arrow element on the correct panel edge,
   * sliding along that edge so it always points at the trigger's center.
   * The arrow is a rotated square; ARROW_HALF px places it half-in, half-out.
   */
  private _positionArrow(
    tRect:  DOMRect,
    coords: { top: number; left: number },
    panelEl: HTMLElement,
  ): void {
    const arrow = this._arrow;
    if (!arrow) return;
    const [side] = (this.placement).split('-');
    const pRect  = panelEl.getBoundingClientRect();
    const MIN    = 10;

    arrow.style.cssText = '';

    if (side === 'top' || side === 'bottom') {
      const cx  = tRect.left + tRect.width  / 2;
      const max = pRect.width  - ARROW_HALF * 2 - MIN;
      const al  = Math.max(MIN, Math.min(cx - coords.left - ARROW_HALF, max));
      arrow.style.left = `${al}px`;
      arrow.style[side === 'bottom' ? 'top' : 'bottom'] = `${-ARROW_HALF}px`;
    } else {
      const cy  = tRect.top + tRect.height / 2;
      const max = pRect.height - ARROW_HALF * 2 - MIN;
      const at  = Math.max(MIN, Math.min(cy - coords.top  - ARROW_HALF, max));
      arrow.style.top = `${at}px`;
      arrow.style[side === 'right' ? 'left' : 'right'] = `${-ARROW_HALF}px`;
    }
  }

  // ── Public API ───────────────────────────────────────────────────

  show():  void { this._cancelTimers(); this._pc.show(); }
  hide():  void { this._cancelTimers(); this._pc.hide(); }
  get isOpen(): boolean { return this._pc.open; }

  // ── Slot detection ───────────────────────────────────────────────

  private _hasContentSlot(): boolean {
    return Array.from(this.children).some(c => c.slot === 'content');
  }

  // ── Render ───────────────────────────────────────────────────────

  protected override render() {
    const isExternal = Boolean(this.triggerId);
    const open       = this._pc.open;

    return html`
      ${!isExternal
        ? html`<slot name="trigger" part="trigger"></slot>`
        : nothing}

      <div
        id="panel"
        part="panel"
        popover="manual"
        role="tooltip"
        class="panel ${open ? 'is-open' : ''}"
        data-placement=${this.placement}
        style="max-width: ${this.maxWidth}"
      >
        ${this.arrow
          ? html`<div id="arrow" part="arrow" class="arrow"></div>`
          : nothing}

        ${this.content && !this._hasContentSlot()
          ? html`<div part="text" class="text">${this.content}</div>`
          : nothing}

        <slot name="content"></slot>
        <slot></slot>
      </div>`;
  }

  static override styles = [styles];
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-tooltip': UwcTooltip; }
}
